/**
 * client/ui/leaderboards.ts
 * Responsabilidad: Gestión del HUD de Ranking en tiempo real (Top 5 jugadores) y futuras tablas globales.
 */

import type { InterpolatedPlayer } from "../types";
import { profileManager } from "../profile";
import { t } from "../i18n";

export class LeaderboardManager {
    private rankingList = document.getElementById("ranking-list");
    private rankingPanel = document.getElementById("ranking");
    private lastRankingSnapshot: string = "";

    constructor() {
        this.initCollapsibleRanking();
    }

    /**
     * Actualiza el Top 5 en el HUD basándose en la lista de jugadores activos.
     * Utiliza un snapshot serializado para evitar thrashing y reflows innecesarios del DOM.
     */
    public update(
        players: Map<string, InterpolatedPlayer> | Iterable<InterpolatedPlayer>,
        localSessionId: string | null
    ): void {
        if (!this.rankingList) return;

        const playerList: InterpolatedPlayer[] = players instanceof Map ? Array.from(players.values()) : Array.from(players);

        // Filtrar jugadores vivos y ordenar por masa descendente
        const sortedPlayers = playerList
            .filter((p) => p.alive && !p.isSpectator)
            .sort((a, b) => b.mass - a.mass);

        const top5 = sortedPlayers.slice(0, 5);

        // Construir snapshot para comparar (sessionId + masa redondeada + kills + racha)
        const snapshot = top5.map((p) => `${p.sessionId}:${Math.round(p.mass)}:${p.kills}:${p.killstreak}`).join("|");
        if (snapshot === this.lastRankingSnapshot) {
            return;
        }
        this.lastRankingSnapshot = snapshot;

        // Actualizar los 5 slots pre-renderizados
        const items = this.rankingList.querySelectorAll(".ranking-item");
        for (let i = 0; i < items.length; i++) {
            const item = items[i] as HTMLElement;
            const player = top5[i];
            const posEl = item.querySelector(".ranking-pos") as HTMLElement | null;
            const nameEl = item.querySelector(".ranking-name") as HTMLElement | null;
            const massEl = item.querySelector(".ranking-mass") as HTMLElement | null;
            if (!posEl || !nameEl || !massEl) continue;

            if (player) {
                posEl.textContent = `#${i + 1}`;
                if (player.sessionId === localSessionId) {
                    nameEl.textContent = `${player.alias} (${t("hud.you")})`;
                    item.classList.add("ranking-self");
                } else {
                    nameEl.textContent = player.alias || "—";
                    item.classList.remove("ranking-self");
                }
                massEl.textContent = player.kills > 0 ? `⚔️${player.kills} · ${Math.round(player.mass)}` : String(Math.round(player.mass));
            } else {
                posEl.textContent = `#${i + 1}`;
                nameEl.textContent = "—";
                massEl.textContent = "0";
                item.classList.remove("ranking-self");
            }
        }

        // Registrar mejor posición alcanzada del jugador local
        for (let i = 0; i < top5.length; i++) {
            if (top5[i].sessionId === localSessionId) {
                profileManager.recordRank(i + 1);
                break;
            }
        }
    }

    private initCollapsibleRanking(): void {
        if (!this.rankingPanel) return;

        if (window.innerWidth <= 768 || window.innerHeight <= 560) {
            this.rankingPanel.classList.add("ranking-collapsed");
        }

        this.rankingPanel.addEventListener("click", () => {
            if (window.innerWidth <= 1024 || window.innerHeight <= 560) {
                this.rankingPanel?.classList.toggle("ranking-collapsed");
            }
        });
    }
}

export interface GlobalLeaderboardProfile {
    userId: string;
    alias: string;
    totalKills: number;
    totalDeaths: number;
    totalGames: number;
    totalTimeMs?: number;
    bestMass: number;
    bestStreak: number;
    bestRank: number;
    updatedAt?: string | null;
}

export interface GlobalLeaderboardResponse {
    topByKills: GlobalLeaderboardProfile[];
    topByMass: GlobalLeaderboardProfile[];
    topByStreak: GlobalLeaderboardProfile[];
    topByTime: GlobalLeaderboardProfile[];
}

export function formatTime(ms: number): string {
    const totalMinutes = Math.floor(Math.max(0, ms) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    const seconds = Math.floor((Math.max(0, ms) % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
}

let currentLeaderboardMode: "season" | "alltime" = "season";

export function getCurrentLeaderboardMode(): "season" | "alltime" {
    return currentLeaderboardMode;
}

export function setCurrentLeaderboardMode(mode: "season" | "alltime"): void {
    currentLeaderboardMode = mode;
}

export async function fetchGlobalLeaderboard(mode: "season" | "alltime" = currentLeaderboardMode): Promise<GlobalLeaderboardResponse | null> {
    currentLeaderboardMode = mode;
    try {
        const res = await fetch(`/api/leaderboard?mode=${mode}`);
        if (!res.ok) {
            throw new Error(`HTTP error ${res.status}`);
        }
        return await res.json();
    } catch (e) {
        console.warn("[Leaderboard] Error al obtener ranking global:", e);
        return null;
    }
}

export function renderGlobalLeaderboardTable(
    tbody: HTMLElement | null,
    items: GlobalLeaderboardProfile[],
    metricKey: "totalKills" | "bestMass" | "bestStreak" | "totalTimeMs"
): void {
    if (!tbody) return;
    tbody.innerHTML = "";

    if (!items || items.length === 0) {
        const row = document.createElement("tr");
        row.innerHTML = `<td colspan="3" class="empty-cell">Sin jugadores registrados aún</td>`;
        tbody.appendChild(row);
        return;
    }

    items.forEach((item, index) => {
        const row = document.createElement("tr");
        if (index === 0) row.classList.add("rank-gold");
        else if (index === 1) row.classList.add("rank-silver");
        else if (index === 2) row.classList.add("rank-bronze");

        const rankBadge = index === 0 ? "🥇 1" : index === 1 ? "🥈 2" : index === 2 ? "🥉 3" : `#${index + 1}`;
        const val = item[metricKey] ?? 0;
        let displayVal = "";
        if (metricKey === "totalKills") {
            displayVal = `⚔️ ${val}`;
        } else if (metricKey === "bestMass") {
            displayVal = `⚪ ${val}`;
        } else if (metricKey === "bestStreak") {
            displayVal = `🔥 ${val}`;
        } else if (metricKey === "totalTimeMs") {
            displayVal = `⌛ ${formatTime(val)}`;
        }

        const tdRank = document.createElement("td");
        tdRank.className = "col-rank";
        tdRank.textContent = rankBadge;

        const tdAlias = document.createElement("td");
        tdAlias.className = "col-alias";
        tdAlias.textContent = item.alias || "Anon";

        const tdVal = document.createElement("td");
        tdVal.className = "col-metric";
        tdVal.textContent = displayVal;

        row.appendChild(tdRank);
        row.appendChild(tdAlias);
        row.appendChild(tdVal);

        tbody.appendChild(row);
    });
}


